/* eslint-disable react/require-default-props */
/* eslint-disable react/no-array-index-key */
import React, { RefObject, useEffect, useRef, useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  useTheme,
  Collapse,
  Button,
  Modal,
  styled,
  SliderProps,
  SlideProps,
  Tooltip,
  Grid
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import RegularInput from '../../../components/input/RegularInput';
import {
  decimalsToHours,
  formatSumTime,
  hoursToDecimals,
  decimalsToMilliseconds,
  formatMillisecondsToHHMM
} from '../../../utils/calendarHelper';
import { ReactComponent as ChevronUpIcon } from '../../../assets/icons/chevron-up.svg';
import { ReactComponent as ChevronDownIcon } from '../../../assets/icons/chevron-down.svg';
import CustomTooltip from '../../../components/shared/CustomTooltip';
import { FormLabel } from '../../../components/form/parts';
import FilterSelect from '../../../components/filters/FilterSelect';
import TimesheetExpandedReviewRow from './TimesheetExpandedReviewRow';

const ButtonContainerModal = styled(Box)<SliderProps>(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  width: theme.spacing(30)
}));

const ModalContainer = styled(Box)<SliderProps>(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.background.paper,
  borderRadius: '0.5em',
  boxShadow: '24px',
  display: 'flex',
  flexDirection: 'column',
  height: '280px',
  justifyContent: 'center',
  padding: '20px',
  position: 'absolute',
  right: '50%',
  textAlign: 'center',
  top: '50%',
  transform: 'translate(50%, -50%)',
  width: '420px'
}));

const DrawerButton = styled(Button)<SlideProps>(({ theme }) => ({
  color: 'white',
  marginLeft: theme.spacing(0.5),
  marginRight: theme.spacing(0.5),
  width: '100%'
}));

type UserType = {
  email: string;
  firstName: string;
  id: string;
  lastName: string;
  phoneNumber: string;
};

interface DataList {
  actualEnd: string;
  actualStart: string;
  actualTotal: number;
  clientName: string;
  closedBy?: string;
  closer?: UserType;
  companyName: string;
  day: string;
  id: number;
  laborHourCost: string;
  laborTotalCost: string;
  millisecondsWorked: string;
  pauseIterations: any[];
  pauseTimeMs: string;
  projectName: string;
  projectType: string;
  scheduledEnd: string;
  scheduledStart: string;
  scheduledTotal: number;
  startedBy?: string;
  starter?: UserType;
  status: string;
  taskName: string;
  technician: string;
  technicianInformation: UserType;
  timeFormat?: string;
  timezone?: string;
  travelTime: number;
  travelTimeInMilliseconds: string;
  type: string;
  typeText: string;
  variance: number;
  varianceActual: string;
  variancePercentage: string;
  workedTimeMs: string;
}

interface Technician {
  dataList: DataList[];
  email: string;
  hourlyRate: string;
  id: string;
  phoneNumber: string;
  technician: string;
  technicianPaymentTotal: string;
  totalHoursScheduled: number;
  totalHoursWorked: number;
  totalPauseMs?: number;
}

interface TimesheetReviewTableProps {
  data: Technician[];
  isLoading: boolean;
  isTechnician?: boolean;
  setUpdateDecisionData: React.Dispatch<any>;
  setUserIndexSelect: React.Dispatch<number>;
  technician: Technician;
}

const smallCell = '1%';

function ListServiceAndTasks({ dataRow }: { dataRow: DataList[] }) {
  return (
    <Box display="flex" flexDirection="column" padding="0px 10px 10px 10px">
      {dataRow.map((item: any, index: number) => (
        <Typography fontSize={15} variant="body1">
          <strong>Service: </strong>{' '}
          {`${item.serviceName || 'No data to display'}`} <br />
          <strong>Task: </strong>{' '}
          {`${
            item.projectType === 'Regular'
              ? item.originalTask
                  .map((element: any) => element.name)
                  ?.join(', ') || 'No data to display'
              : item.originalTask || 'No data to display'
          }`}
        </Typography>
      ))}
    </Box>
  );
}

function CustomTable(props: {
  boxHeight: number;
  confirmModal: (val: any) => void;
  isTechnician: boolean;
  rows: DataList[];
  technician?: Technician;
}) {
  const { rows, confirmModal, boxHeight, isTechnician, technician } = props;
  const expandedCellRef: RefObject<HTMLDivElement> =
    useRef<HTMLDivElement>(null);
  const [expandedCellHeight, setExpandedCellHeight] = React.useState<number>(0);
  const theme = useTheme();
  const { t } = useTranslation();
  const [openRow, setOpenRow] = useState<number | null>(null);
  const [inputValues, setInputValues] = useState<{
    [key: number]: number | string;
  }>({});

  const toggleRow = (index: number) => {
    if (openRow === index) {
      setOpenRow(null);
      return;
    }
    setOpenRow(index);
  };

  const handleInputChange = (index: number, value: string) => {
    setInputValues((prevValues) => ({
      ...prevValues,
      [index]: value
    }));
  };

  useEffect(() => {
    if (rows) {
      rows.map((item, index) =>
        handleInputChange(index, item.millisecondsWorked)
      );
    }
  }, [rows]);

  const handleValueInput = (value: any) => {
    let millisecondsWorked = value;
    if (typeof millisecondsWorked === 'string') {
      if (!millisecondsWorked.includes(':')) {
        millisecondsWorked = decimalsToHours(millisecondsWorked);
      }
    }
    if (typeof millisecondsWorked === 'number') {
      millisecondsWorked = decimalsToHours(millisecondsWorked);
    }
    return millisecondsWorked;
  };

  return (
    <TableContainer
      style={{
        border: '1px solid #E2E4EA',
        borderRadius: '10px',
        marginTop: 16,
        overflowX: isMobile ? 'scroll' : 'initial'
      }}
    >
      <Table stickyHeader>
        <TableHead
          sx={[
            !isMobile && {
              position: 'sticky',
              top: `${boxHeight + 90}px`,
              zIndex: 1
            }
          ]}
        >
          <TableRow>
            {[
              t('TIMESHEET.date'),
              t('TIMESHEET.assignment'),
              `${t('TIMESHEET.travelTime')}\n${t('TIMESHEET.hhmmss')}`,
              `${t('TIMESHEET.schedule')}\n${t('TIMESHEET.hhmm')}`,
              `${t('TIMESHEET.pauseTime')}\n${t('TIMESHEET.hhmmss')}`,
              `${t('TIMESHEET.workedTime')}\n${t('TIMESHEET.hhmmss')}`,
              `${t('TIMESHEET.visitTime')}\n${t('TIMESHEET.hhmmss')}`,
              `${t('TIMESHEET.variance')}\n${t('TIMESHEET.hhmm')}`
            ].map((col) => (
              <TableCell
                align="center"
                key={col}
                sx={{
                  backgroundColor: '#F5F6F8',
                  borderBottom: '1px solid #E2E4EA',
                  color: '#7D8090',
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  whiteSpace: 'pre-line'
                }}
              >
                {col}
              </TableCell>
            ))}
            {!isTechnician && (
              <TableCell
                sx={{ backgroundColor: '#F5F6F8' }}
                width={smallCell}
              />
            )}
            {!isTechnician && (
              <TableCell
                sx={{ backgroundColor: '#F5F6F8' }}
                width={smallCell}
              />
            )}
            <TableCell sx={{ backgroundColor: '#F5F6F8' }} width={smallCell} />
          </TableRow>
        </TableHead>
        {rows.length > 0 ? (
          <TableBody>
            {rows.map((item, index) => {
              return (
                <>
                  <TableRow
                    sx={{
                      '& > *': { borderBottom: 'unset' },
                      ...(!isMobile &&
                        openRow === index && {
                          '&::after': {
                            bottom: `-${expandedCellHeight}px`,
                            boxShadow: '#DEDEDE 0px 0px 20px',
                            content: "''",
                            left: 0,
                            pointerEvents: 'none',
                            position: 'absolute',
                            right: 0,
                            top: 0
                          }
                        }),
                      '&:hover': { backgroundColor: '#FAFBFC' },
                      borderBottom: '1px solid #F0F1F3',
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                  >
                    <TableCell align="center" onClick={() => toggleRow(index)}>
                      {item?.day}
                    </TableCell>

                    <TableCell align="center" onClick={() => toggleRow(index)}>
                      {`${item?.clientName}  ${item?.projectName}  ${
                        item?.projectType === 'Ad-Hoc'
                          ? ` - ${t('TIMESHEET.adhoc')}`
                          : ''
                      }   `}
                      <CustomTooltip
                        backgroundColor={theme.palette.grey[600]}
                        borderColor={theme.palette.grey[600]}
                        component={<ListServiceAndTasks dataRow={[item]} />}
                      >
                        <div
                          style={{
                            display: 'inline-flex'
                          }}
                        >
                          <i
                            className="custom-icon icon-info"
                            style={{
                              color: `${theme.palette.informative[600]}`,
                              fontSize: 21,
                              position: 'relative',

                              top: '3px'
                            }}
                          />
                        </div>
                      </CustomTooltip>
                    </TableCell>

                    <TableCell align="center" onClick={() => toggleRow(index)}>
                      {item?.travelTimeInMilliseconds}
                    </TableCell>
                    <TableCell align="center" onClick={() => toggleRow(index)}>
                      {decimalsToHours(item?.scheduledTotal)}
                    </TableCell>
                    <TableCell align="center" onClick={() => toggleRow(index)}>
                      {item?.pauseTimeMs}
                    </TableCell>
                    <TableCell align="center" onClick={() => toggleRow(index)}>
                      {item?.workedTimeMs}
                    </TableCell>
                    <TableCell align="center">
                      {isTechnician ? (
                        item?.millisecondsWorked
                      ) : (
                        <RegularInput
                          customStyle={{ width: '100px' }}
                          onChange={(e) => handleInputChange(index, e)}
                          type="text"
                          value={handleValueInput(inputValues[index]) || ''}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center" onClick={() => toggleRow(index)}>
                      {(() => {
                        // Calculate variance as Visit Time - Scheduled Time
                        // millisecondsWorked is in HH:MM:SS format, convert to hours
                        const visitTimeInHours = item?.millisecondsWorked
                          ? hoursToDecimals(item.millisecondsWorked)
                          : 0;
                        const scheduledTime = item?.scheduledTotal || 0;
                        const variance = visitTimeInHours - scheduledTime;

                        return item?.projectType === 'Ad-Hoc'
                          ? decimalsToHours(variance)
                          : decimalsToHours(variance);
                      })()}
                    </TableCell>

                    {!isTechnician && (
                      <TableCell align="center">
                        <Tooltip arrow title="Reject Actual">
                          <Button
                            onClick={() => {
                              setOpenRow(null);
                              let actualTotal = item.scheduledTotal;
                              if (actualTotal < 0) {
                                actualTotal *= -1;
                              }
                              actualTotal = Number(actualTotal.toFixed(2));
                              confirmModal({
                                actualTotal,
                                approvalStatus: 'Denied',
                                taskIterationId: item?.id,
                                type: item?.type
                              });
                            }}
                            size="small"
                            sx={{
                              '&:hover': { backgroundColor: '#FEF2F2' },
                              borderRadius: '6px',
                              color: '#EF4444',
                              minWidth: 0,
                              p: '4px 8px'
                            }}
                          >
                            <i
                              className="custom-icon icon-clear"
                              style={{ fontSize: 16 }}
                            />
                          </Button>
                        </Tooltip>
                      </TableCell>
                    )}

                    {!isTechnician && (
                      <TableCell align="center">
                        <Tooltip arrow title="Accept Actual">
                          <Button
                            onClick={() => {
                              setOpenRow(null);
                              let actualTotal = inputValues[index];
                              if (typeof actualTotal === 'string') {
                                if (actualTotal.includes(':')) {
                                  actualTotal = hoursToDecimals(actualTotal);
                                }
                                actualTotal = Number(actualTotal);
                              }
                              if (actualTotal < 0) {
                                actualTotal *= -1;
                              }
                              actualTotal = Number(actualTotal);
                              confirmModal({
                                actualTotal,
                                approvalStatus: 'Approved',
                                taskIterationId: item?.id,
                                type: item?.type
                              });
                            }}
                            size="small"
                            sx={{
                              '&:hover': { backgroundColor: '#F0FDF4' },
                              borderRadius: '6px',
                              color: '#22C55E',
                              minWidth: 0,
                              p: '4px 8px'
                            }}
                          >
                            <i
                              className="custom-icon icon-active"
                              style={{ fontSize: 16 }}
                            />
                          </Button>
                        </Tooltip>
                      </TableCell>
                    )}

                    <TableCell align="center" onClick={() => toggleRow(index)}>
                      {openRow === index ? (
                        <ChevronUpIcon />
                      ) : (
                        <ChevronDownIcon />
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      style={{
                        backgroundColor: `${theme.palette.background.table}`,
                        marginBottom: 10,
                        paddingBottom: 0,
                        paddingTop: 0
                      }}
                    >
                      <Collapse
                        unmountOnExit
                        in={openRow === index}
                        onEntered={() =>
                          setExpandedCellHeight(
                            expandedCellRef.current?.clientHeight ?? 0
                          )
                        }
                        ref={expandedCellRef}
                        timeout="auto"
                      >
                        <TimesheetExpandedReviewRow dataList={item} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </>
              );
            })}
          </TableBody>
        ) : (
          /* Empty state */
          <TableBody>
            <TableRow>
              <TableCell colSpan={11} sx={{ textAlign: 'center' }}>
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={theme.spacing(1)}
                  padding={theme.spacing(8)}
                >
                  <Typography
                    color={theme.palette.secondary.main}
                    variant="primary-bold"
                  >
                    {t('TIMESHEET.noTimeVariance', [
                      technician?.technician ?? 'This Technician'
                    ])}
                  </Typography>
                  <Typography
                    color={theme.palette.secondary.main}
                    variant="primary"
                  >
                    {t('TIMESHEET.noTimeVarianceDescription')}
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          </TableBody>
        )}
      </Table>
    </TableContainer>
  );
}

const TimesheetReviewTable: React.FC<TimesheetReviewTableProps> =
  function TimesheetReviewTable({
    data,
    setUpdateDecisionData,
    isLoading,
    isTechnician = false,
    setUserIndexSelect,
    technician
  }) {
    const boxRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
    const theme = useTheme();
    const { t } = useTranslation();
    const [confirmModal, setConfirmModal] = useState<boolean>(false);
    const [decisionData, setDecisionData] = useState<any>({});
    const [boxHeight, setBoxHeight] = useState<number>(0);
    const [projectType, setProjectType] = useState<string>('All');

    const adhocTasks = technician?.dataList?.filter(
      (item) => item.type === 'taskIteration'
    );
    const reoccurringTasks = technician?.dataList?.filter(
      (item) => item.type === 'calendarEventIteration'
    );
    const tableDataSet = useMemo(() => {
      if (projectType === 'taskIteration') return adhocTasks;
      if (projectType === 'calendarEventIteration') return reoccurringTasks;
      return technician?.dataList ?? [];
    }, [projectType, adhocTasks, reoccurringTasks]);

    const variances = technician?.dataList?.map((item) => {
      let formatedVariance = item.varianceActual
        ? Number(item.varianceActual)
        : 0;
      if (item.projectType === 'Regular') {
        formatedVariance = hoursToDecimals(formatSumTime(formatedVariance));
      }
      return formatedVariance;
    });
    const totalVariance = variances?.reduce((acc, variance) => {
      if (variance <= 0) {
        return acc + -1 * variance;
      }
      return acc + variance;
    }, 0);
    const varianceAverage = totalVariance / (technician?.dataList?.length || 0);

    const handleOpenModal = (data: any) => {
      setConfirmModal(true);
      setDecisionData(data);
    };

    useEffect(() => {
      const updateBoxHeight = () => {
        if (boxRef.current) {
          setBoxHeight(boxRef?.current?.clientHeight);
        }
      };
      updateBoxHeight();
      window.addEventListener('resize', updateBoxHeight);
      return () => {
        window.removeEventListener('resize', updateBoxHeight);
      };
    }, []);

    return (
      <Box>
        {/* ── Technician selector toolbar ── */}
        {isTechnician ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5">{technician.technician}</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              alignItems: 'center',
              backgroundColor: 'white',
              borderBottom: '1px solid #F0F1F3',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              mb: 2.5,
              pb: 2,
              position: 'sticky',
              top: 0,
              zIndex: 2
            }}
          >
            {/* Resources dropdown */}
            <Box sx={{ minWidth: 200 }}>
              <Typography
                sx={{
                  color: '#7D8090',
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  mb: 0.5,
                  textTransform: 'uppercase'
                }}
              >
                {t('TIMESHEET.resources')}
              </Typography>
              <FilterSelect
                label={t('TIMESHEET.resources')}
                onChange={(event) =>
                  setUserIndexSelect(
                    data.findIndex((val) => val.id === event.target.value)
                  )
                }
                options={data.map((item) => ({
                  ...item,
                  name: `${item.technician} (${item.dataList?.length ?? 0})`
                }))}
                sx={{ backgroundColor: '#F5F6F8' }}
                value={technician.id}
                width="100%"
              />
            </Box>

            {/* Project Type filter */}
            <Box sx={{ minWidth: 140 }}>
              <Typography
                sx={{
                  color: '#7D8090',
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  mb: 0.5,
                  textTransform: 'uppercase'
                }}
              >
                {t('TIMESHEET.projectType')}
              </Typography>
              <FilterSelect
                label={t('TIMESHEET.projectType')}
                onChange={(event) => setProjectType(event.target.value)}
                options={[
                  { id: 'All', name: 'All' },
                  { id: 'taskIteration', name: 'Ad-Hoc' },
                  { id: 'calendarEventIteration', name: 'Regular' }
                ]}
                sx={{ backgroundColor: '#F5F6F8' }}
                value={projectType}
                width="100%"
              />
            </Box>

            {/* Contact buttons */}
            <Box
              sx={{ alignItems: 'center', display: 'flex', gap: 1, ml: 'auto' }}
            >
              <Button
                href={`tel:${technician.phoneNumber}`}
                size="small"
                sx={{
                  '&:hover': {
                    backgroundColor: '#F5F6F8',
                    borderColor: '#C8CBD4'
                  },
                  border: '1px solid #E2E4EA',
                  borderRadius: '8px',
                  color: '#5A6478',
                  minWidth: 0,
                  p: '6px 10px'
                }}
              >
                <i
                  className="custom-icon icon-classic-phone"
                  style={{ fontSize: 16 }}
                />
              </Button>
              <Button
                href={`mailto:${technician.email}`}
                size="small"
                sx={{
                  '&:hover': {
                    backgroundColor: '#F5F6F8',
                    borderColor: '#C8CBD4'
                  },
                  border: '1px solid #E2E4EA',
                  borderRadius: '8px',
                  color: '#5A6478',
                  minWidth: 0,
                  p: '6px 10px'
                }}
              >
                <i
                  className="custom-icon icon-email"
                  style={{ fontSize: 16 }}
                />
              </Button>
            </Box>
          </Box>
        )}

        {/* ── KPI summary cards ── */}
        <Box
          ref={boxRef}
          sx={{
            backgroundColor: 'white',
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: 'repeat(6, 1fr)',
            mb: 2.5,
            pb: 1,
            position: 'sticky',
            pt: 0.5,
            top: isTechnician ? 0 : '62px',
            zIndex: 2
          }}
        >
          {[
            {
              accent: 'linear-gradient(90deg, #005DA2, #0078cc)',
              label: t('TIMESHEET.totalTasks'),
              value: technician.dataList.length.toString()
            },
            {
              accent: 'linear-gradient(90deg, #6C63FF, #9B8FF7)',
              label: t('TIMESHEET.totalTime'),
              value: decimalsToHours(technician.totalHoursScheduled)
            },
            {
              accent: 'linear-gradient(90deg, #189A90, #22c4b8)',
              label: t('TIMESHEET.totalWorked'),
              value: decimalsToHours(technician.totalHoursWorked)
            },
            {
              accent: 'linear-gradient(90deg, #F59E0B, #FCD34D)',
              label: t('TIMESHEET.varianceAverage'),
              value: decimalsToHours(varianceAverage)
            },
            {
              accent: 'linear-gradient(90deg, #EF4444, #F87171)',
              label: t('TIMESHEET.varianceTotal'),
              value: decimalsToHours(totalVariance)
            },
            {
              accent: 'linear-gradient(90deg, #64748B, #94A3B8)',
              label: t('TIMESHEET.totalPause'),
              value: formatMillisecondsToHHMM(
                technician.totalPauseMs?.toString() || '0'
              )
            }
          ].map((card) => (
            <Box
              key={card.label}
              sx={{
                backgroundColor: 'white',
                border: '1px solid #E2E4EA',
                borderRadius: '10px',
                boxShadow: '0px 1px 3px rgba(15,28,46,0.06)',
                overflow: 'hidden',
                p: 2,
                position: 'relative'
              }}
            >
              <Box
                sx={{
                  background: card.accent,
                  borderRadius: '6px 6px 0 0',
                  height: 3,
                  left: 0,
                  position: 'absolute',
                  right: 0,
                  top: 0
                }}
              />
              <Typography
                sx={{
                  color: '#7D8090',
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  mb: 0.5,
                  textTransform: 'uppercase'
                }}
              >
                {card.label}
              </Typography>
              <Typography
                sx={{
                  color: '#0f1c2e',
                  fontSize: '18px',
                  fontWeight: 700,
                  letterSpacing: '-0.3px'
                }}
              >
                {isLoading ? '—' : card.value}
              </Typography>
            </Box>
          ))}
        </Box>

        <CustomTable
          boxHeight={boxHeight}
          confirmModal={(val: any) => handleOpenModal(val)}
          isTechnician={isTechnician}
          rows={tableDataSet}
          technician={technician}
        />

        <Modal onClose={() => setConfirmModal(false)} open={confirmModal}>
          <ModalContainer>
            <Typography color="secondary.400" variant="modalTitle">
              <i
                className="custom-icon icon-info"
                style={{
                  color:
                    decisionData.approvalStatus === 'Approved'
                      ? theme.palette.primary.main
                      : theme.palette.error.main,
                  fontSize: 40
                }}
              />
              <br />
              <br />
              {`
            ${formatMillisecondsToHHMM(
              decimalsToMilliseconds(decisionData.actualTotal).toString()
            )} will set as actual time`}
            </Typography>
            <br />
            <ButtonContainerModal>
              <>
                <DrawerButton
                  onClick={() => {
                    setConfirmModal(false);
                    setDecisionData({});
                  }}
                  sx={{ height: 46, width: 114 }}
                  variant={
                    decisionData.approvalStatus === 'Approved'
                      ? 'primary-outlined'
                      : 'destructive-outlined'
                  }
                >
                  <Typography
                    color={
                      decisionData.approvalStatus === 'Approved'
                        ? 'primary'
                        : 'error'
                    }
                  >
                    {t('MODAL.cancel')}
                  </Typography>
                </DrawerButton>
                <DrawerButton
                  onClick={() => {
                    setUpdateDecisionData({
                      ...decisionData,
                      actualTotal: Number(decisionData.actualTotal.toFixed(2))
                    });
                    setConfirmModal(false);
                    setDecisionData({});
                  }}
                  sx={{ height: 46, width: 114 }}
                  variant={
                    decisionData.approvalStatus === 'Approved'
                      ? 'primary'
                      : 'destructive'
                  }
                >
                  <Typography>
                    {decisionData.approvalStatus === 'Approved'
                      ? 'Confirm'
                      : 'Reject'}
                  </Typography>
                </DrawerButton>
              </>
            </ButtonContainerModal>
          </ModalContainer>
        </Modal>
      </Box>
    );
  };

export default TimesheetReviewTable;
